import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Save, X, Plus, Sliders } from 'lucide-react';

/**
 * Editable review dialog used after an upload's dry_run to let coaches fix
 * typos / missing fields before committing the import.  Works for any row
 * shape — pass `columns` (array of {key, label}) plus `rows` array of dicts.
 *
 * `sourceHeaders` (optional): raw column headers from the coach's uploaded file
 * that did NOT auto-match to a canonical field.  We surface them in an "Advanced"
 * panel so the coach can manually map, e.g. "Emergency Contact" → parent_phone.
 * `rawRows` (optional): the coach's file rows keyed by their original headers,
 * used to populate mapped values when the coach picks a manual mapping.
 */
export default function EditableImportReview({
  open,
  onClose,
  title = 'Review Imported Rows',
  columns = [],
  initialRows = [],
  onConfirm,
  isImporting = false,
  sourceHeaders = [],
  rawRows = [],
  onSaveMappings = null,  // optional (mappings: {srcHeader: canonicalField}) => Promise<void>
}) {
  const [rows, setRows] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [manualMap, setManualMap] = useState({});
  const [rememberMappings, setRememberMappings] = useState(true);
  const [isSavingMappings, setIsSavingMappings] = useState(false);

  useEffect(() => {
    if (open) {
      setRows(initialRows.map((r) => ({ ...r })));
      setManualMap({});
      setShowAdvanced(false);
      setRememberMappings(true);
    }
  }, [open, initialRows]);

  const applyManualMap = async () => {
    // For every (sourceHeader -> field) mapping, copy rawRows[i][sourceHeader] into rows[i][field]
    if (rawRows && rawRows.length > 0) {
      setRows((prev) => prev.map((row, idx) => {
        const rawRow = rawRows[idx] || {};
        const updated = { ...row };
        Object.entries(manualMap).forEach(([srcHeader, field]) => {
          if (!field || field === '__none__') return;
          const val = rawRow[srcHeader];
          if (val !== undefined && val !== null && String(val).trim() !== '') {
            updated[field] = String(val).trim();
          }
        });
        return updated;
      }));
    }

    // Optionally persist mappings for next time (auto-map memory)
    if (rememberMappings && onSaveMappings) {
      const toSave = {};
      Object.entries(manualMap).forEach(([srcHeader, field]) => {
        if (field && field !== '__none__') toSave[srcHeader] = field;
      });
      if (Object.keys(toSave).length > 0) {
        setIsSavingMappings(true);
        try {
          await onSaveMappings(toSave);
        } catch (e) {
          // ignore — surface via toast in caller if desired
        } finally {
          setIsSavingMappings(false);
        }
      }
    }
  };

  const unmappedHeaders = useMemo(() => {
    // Show headers that don't already correspond to a canonical field.
    const canonicalLabels = new Set(columns.flatMap((c) => [c.key.toLowerCase(), c.label.toLowerCase()]));
    return (sourceHeaders || []).filter((h) => h && !canonicalLabels.has(String(h).toLowerCase()));
  }, [sourceHeaders, columns]);

  const updateCell = (rowIdx, key, value) => {
    setRows((prev) => {
      const next = [...prev];
      next[rowIdx] = { ...next[rowIdx], [key]: value };
      return next;
    });
  };

  const removeRow = (rowIdx) => {
    setRows((prev) => prev.filter((_, i) => i !== rowIdx));
  };

  const addRow = () => {
    const blank = columns.reduce((acc, c) => ({ ...acc, [c.key]: '' }), {});
    setRows((prev) => [...prev, blank]);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-6xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="import-review-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl uppercase">{title}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          We matched columns to fields loosely. Fix any mistakes below (edit cells directly, delete bad rows,
          or add new ones) and hit <span className="font-semibold">Confirm Import</span> when it's ready.
        </p>

        {unmappedHeaders.length > 0 && (
          <div className="border rounded-md" data-testid="advanced-mapper-panel">
            <button
              type="button"
              className="w-full flex items-center justify-between px-3 py-2 text-left text-sm font-semibold hover:bg-muted/40"
              onClick={() => setShowAdvanced((v) => !v)}
              data-testid="toggle-advanced-mapper"
            >
              <span className="flex items-center gap-2">
                <Sliders className="h-4 w-4" />
                Advanced: {unmappedHeaders.length} column{unmappedHeaders.length === 1 ? '' : 's'} didn't auto-match
              </span>
              <span className="text-xs text-muted-foreground">{showAdvanced ? 'Hide' : 'Show'}</span>
            </button>
            {showAdvanced && (
              <div className="p-3 border-t bg-muted/20 space-y-2">
                <p className="text-xs text-muted-foreground">
                  Pick which of our fields each column below should feed into. Click "Apply Mappings" to fill those columns in the table for every row.
                </p>
                {unmappedHeaders.map((header) => (
                  <div key={header} className="flex items-center gap-3">
                    <span className="text-sm font-medium min-w-[180px] truncate" title={header}>{header}</span>
                    <span className="text-muted-foreground text-xs">→</span>
                    <Select
                      value={manualMap[header] || '__none__'}
                      onValueChange={(v) => setManualMap((prev) => ({ ...prev, [header]: v }))}
                    >
                      <SelectTrigger className="max-w-[280px]" data-testid={`mapper-select-${header}`}>
                        <SelectValue placeholder="Choose field..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Ignore this column —</SelectItem>
                        {columns.map((c) => (
                          <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
                <div className="flex items-center justify-between gap-2">
                  {onSaveMappings ? (
                    <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                      <input
                        type="checkbox"
                        checked={rememberMappings}
                        onChange={(e) => setRememberMappings(e.target.checked)}
                        data-testid="remember-mappings-checkbox"
                      />
                      Remember these mappings for this team's next upload
                    </label>
                  ) : <span />}
                  <Button size="sm" variant="outline" onClick={applyManualMap} disabled={isSavingMappings} data-testid="apply-mapper-btn">
                    {isSavingMappings ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
                    Apply Mappings
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex-1 overflow-auto border rounded-md">
          <table className="w-full text-sm">
            <thead className="bg-muted sticky top-0">
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className="text-left px-2 py-2 font-semibold text-xs uppercase text-muted-foreground border-b">
                    {c.label}
                  </th>
                ))}
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={columns.length + 1} className="text-center py-6 text-muted-foreground italic">
                    No rows to import.  Add one below to start.
                  </td>
                </tr>
              )}
              {rows.map((row, rIdx) => (
                <tr key={rIdx} className="border-b hover:bg-muted/20" data-testid={`import-row-${rIdx}`}>
                  {columns.map((c) => (
                    <td key={c.key} className="p-1">
                      <Input
                        value={row[c.key] ?? ''}
                        onChange={(e) => updateCell(rIdx, c.key, e.target.value)}
                        className="h-8 text-sm"
                        data-testid={`import-cell-${rIdx}-${c.key}`}
                      />
                    </td>
                  ))}
                  <td className="p-1 text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeRow(rIdx)}
                      className="h-8 w-8 text-red-500 hover:text-red-700"
                      data-testid={`import-row-remove-${rIdx}`}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between gap-2 pt-2">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Label className="text-xs">{rows.length} row{rows.length === 1 ? '' : 's'}</Label>
            <Button variant="outline" size="sm" onClick={addRow} data-testid="import-add-row-btn">
              <Plus className="h-3 w-3 mr-1" />
              Add row
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onClose} disabled={isImporting}>Cancel</Button>
            <Button
              onClick={() => onConfirm(rows)}
              disabled={isImporting || rows.length === 0}
              data-testid="confirm-import-btn"
            >
              {isImporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {isImporting ? 'Importing...' : `Confirm Import (${rows.length})`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
